import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import { Icon } from 'components';
import UserSearch from 'components/UserSearch';
import useFlavorInfo from 'hooks/useFlavorInfo';
import LocaleContext from 'locales';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { AiOutlineCaretDown } from 'react-icons/ai';
import ConfigContext from 'store/ConfigContext';
import { v4 as uuidv4 } from 'uuid';
import { OwnDesktops, RequestInfo, TimePreset } from './components';
import FormItemApprovalLine from './components/FormItemApprovalLine';
import FormItemSelectVdGroup from './components/FormItemSelectVdGroup';

/**
 * 컴포넌트: VD 생성 상세
 * @param {string} [id] 상세 화면 id
 * @param {string} type 서비스 신청 타입
 * @param {fucntion} onDetailClose 상세 화면 닫힐 때 콜백 함수
 * @param {function} setIsWaitState isWaitState 상태를 업데이트한다.
 * @param {boolean} isCreate 생성인가?
 * @param {object} detailData Detail 컴포넌트에서 전달된 상세 데이터
 * @returns ReactNode
 */
function DesktopCreate(props) {
  const { id, type, form, setIsWaitState, isCreate, detailData, onFinishDetail, backConf } = props;

  // states
  const [visibleOwnDesktops, setVisibleOwnDesktops] = React.useState(false);
  const [userSearchNamePath, setUserSearchNamePath] = useState(undefined); // UserSearch를 설정할 폼의 네임 패스

  // hooks
  const [{ flavorOptions }, getSelectedFlavor] = useFlavorInfo();
  const { locale } = useContext(LocaleContext);
  const config = useContext(ConfigContext);

  const isEnsolSite = config.siteName === 'lgensol-usa'; // 사이트 엔솔 북미 여부 체크

  useEffect(() => {
    if (!id) {
      const applicant = localStorage.getItem('userId');
      form.setFieldsValue({
        applicant,
        type: 'approval.types.create-vd',
        contents: [
          {
            id: Date.now() + 0,
          },
        ],
      });

      return;
    }
    if (detailData) {
      let approveDate = detailData?.approveDate;
      if (approveDate !== null && approveDate !== undefined) {
        approveDate = moment(approveDate);
      }

      // 사용자 결재인 경우
      if (detailData.approvalLines) {
        const newApprovalLines = detailData.approvalLines.map((el) => ({
          ...el,
          key: uuidv4(),
        }));
        detailData.approvalLines = newApprovalLines;
      }

      form.setFieldsValue({
        ...detailData,
        vdGroupId: detailData.contents[0].vdGroupId,
        approveDate,
        applyDate: moment(detailData.applyDate).format('YYYY-MM-DD HH:mm:ss'),
      });

      if (setIsWaitState) {
        setIsWaitState(detailData.state === 'approval.state.waiting');
      }
    }
  }, [id, detailData]);

  // 저장 시
  const onFinish = async (values) => {
    const { contents, approveDate, ...fields } = values;
    const [content] = contents;
    const flavorInfo = getSelectedFlavor(content.flavorName);

    contents[0].vdGroupId = fields.vdGroupId;
    contents[0].vdGroupName = fields.vdGroupName;
    delete fields.vdGroupId;
    delete fields.vdGroupName;

    const payload = {
      ...fields,
      contents: [{ ...content, ownDesktop: '', ...flavorInfo }],
      approveDate: approveDate?.format('YYYY-MM-DD HH:mm:ss') || approveDate, // #193685
    };
    onFinishDetail(payload);
  };

  // 사용자 선택되었을 때
  const onUserSearchSelected = (selectedUser) => {
    // 선택 취소면 아무것도 안한다.
    if (!selectedUser) {
      setUserSearchNamePath(undefined);
      return;
    }
    const result = { [userSearchNamePath]: selectedUser.sAMAccountName };
    form.setFieldsValue(result);
    setUserSearchNamePath(undefined);
  };

  return (
    <Row gutter={[0, 24]}>
      <Col span={24}>
        <Typography.Title level={5}>{locale.resource['txt.contents']}</Typography.Title>
        <Card bordered={false}>
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 19 }}
            onFinish={onFinish}
          >
            <RequestInfo readOnly={!isCreate} />
            <Form.Item label={locale.resource['txt.approval-type']}>
              {locale.resource['txt.vd-creation']}
            </Form.Item>
            <Form.Item label={locale.resource['txt.approval-type']} name="type" hidden>
              <Input value="approval.types.create-vd" />
            </Form.Item>
            <Form.Item label={locale.resource['txt.user-and-id']} required>
              <Row gutter={[0, 4]}>
                <Col span={24}>
                  <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Form.Item
                      noStyle
                      name="user"
                      rules={[{ required: true, message: locale.resource['txt.required-field'] }]}
                    >
                      <Input.Search
                        readOnly
                        disabled={!isCreate}
                        onSearch={() => setUserSearchNamePath(['user'])}
                      />
                    </Form.Item>
                    <UserSearch visible={userSearchNamePath} onSelected={onUserSearchSelected} />
                    <Button
                      type="text"
                      className="ant-dropdown-link"
                      onClick={() => {
                        setVisibleOwnDesktops(!visibleOwnDesktops);
                      }}
                    >
                      {locale.resource['txt.assign-vd']}
                      <Icon name={<AiOutlineCaretDown className="anticon anticon-down" />} />
                    </Button>
                  </Space>
                </Col>
                {visibleOwnDesktops && (
                  <Col span={24}>
                    <OwnDesktops readOnly user={form.getFieldValue('user')} apprType={type} />
                  </Col>
                )}
              </Row>
            </Form.Item>
            <FormItemSelectVdGroup form={form} isCreate={isCreate} isEnsolSite={isEnsolSite} />
            <Form.Item
              label={locale.resource['txt.desktop-info']}
              name="contents"
              valuePropName="dataSource"
              required
            >
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                columns={[
                  {
                    title: locale.resource['txt.flavor'],
                    dataIndex: 'flavorName',
                    render: (text, record, index) => {
                      const flavorInfo = getSelectedFlavor(text);
                      const isFlavorUnavailable = text && !flavorInfo && isCreate; // 플레이버 이름 O, 정보 X > 사용할 수 없는 플레이버로 판별.
                      return (
                        <Form.Item
                          name={['contents', `${index}`, 'flavorName']}
                          validateStatus={isFlavorUnavailable && 'error'}
                          help={
                            isFlavorUnavailable ? locale.resource['txt.flavor-unavailable'] : null
                          }
                          rules={[
                            { required: true, message: locale.resource['txt.required-field'] },
                          ]}
                          noStyle
                        >
                          <Select
                            disabled
                            placeholder={locale.resource['txt.select-option-required']}
                            options={flavorOptions}
                          />
                        </Form.Item>
                      );
                    },
                  },
                  {
                    title: locale.resource['txt.disk'],
                    dataIndex: 'disk',
                    width: 120,
                    readOnly: true,
                    render: (text, record, index) => {
                      return !isCreate ? (
                        `${text} ${locale.resource['txt.gb']}`
                      ) : (
                        <Form.Item
                          name={['contents', `${index}`, 'disk']}
                          noStyle
                          initialValue={text}
                        >
                          <InputNumber
                            addonAfter={locale.resource['txt.gb']}
                            min={0}
                            disabled
                            onChange={() => {
                              form.setFields([
                                { name: ['contents', `${index}`, 'disk'], errors: [] },
                              ]);
                            }}
                          />
                        </Form.Item>
                      );
                    },
                  },
                ]}
              />
            </Form.Item>
            <Form.Item
              label={locale.resource['txt.execution-date']}
              name="approveDate"
              extra={locale.resource['txt.excution-date-msg']}
              rules={[
                () => ({
                  validator(_, value) {
                    if (value > moment() || !value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(locale.resource['txt.excution-date-val']));
                  },
                }),
              ]}
              initialValue={null} // #193685
            >
              <DatePicker
                showTime={{
                  defaultValue: moment('00:00', 'HH:mm'),
                  format: 'HH:mm',
                }}
                inputReadOnly={1}
                disabled={!isCreate}
                placeholder=""
                disabledDate={(date) => date && date < moment().startOf('day')}
                showNow={false}
                format="YYYY-MM-DD HH:mm"
                renderExtraFooter={() => (
                  <TimePreset
                    onSelect={(time) => {
                      form.setFieldsValue({ approveDate: time });
                    }}
                  />
                )}
              />
            </Form.Item>
            <FormItemApprovalLine isCreate={isCreate} form={form} backConf={backConf} type={type} />
          </Form>
        </Card>
      </Col>
    </Row>
  );
}

export default DesktopCreate;
